// Geographic points typed or pasted by a person (a location's lintang/bujur).

export interface LatLng { lat: number; lng: number }
export interface Bounds { south: number; north: number; west: number; east: number }

/** One coordinate as people write it: "-6.2915", "-6,2915" (id-ID decimal comma), "106.8020"; null when it is not one. */
export function parseCoordinate(s: string): number | null {
  const t = s.trim().replace(/^\+/, '').replace(/^\u2212/, '-');
  if (!/^-?\d{1,3}(?:[.,]\d+)?$/.test(t)) return null;
  return Number(t.replace(',', '.'));
}

/**
 * A pasted or typed point, in the shapes Google Maps and people use:
 * - "-6.2915, 106.8020" (copied from Google Maps), "-6.2915,106.8020", "-6.2915 106.8020", "-6.2915;106.8020";
 * - with decimal commas, separated by a space or a semicolon: "-6,2915 106,8020", "-6,2915; 106,8020".
 * Null when it is not one (a single number, words, three numbers, out of the world's range).
 */
export function parseLatLng(text: string): LatLng | null {
  const t = text.trim().replace(/[()]/g, '');
  if (!t) return null;
  // the separator, most explicit first: a semicolon, a comma and a space (Google), whitespace, a lone comma
  const parts = t.includes(';') ? t.split(/\s*;\s*/) : /,\s/.test(t) ? t.split(/\s*,\s+/) : /\s/.test(t) ? t.split(/\s+/) : t.split(',');
  if (parts.length !== 2) return null;
  const lat = parseCoordinate(parts[0]);
  const lng = parseCoordinate(parts[1]);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** The point rounded to `decimals` places (6 = about 10 cm, more than any phone fix is worth). */
export const roundLatLng = (p: LatLng, decimals: number): LatLng => {
  const k = 10 ** decimals;
  return { lat: Math.round(p.lat * k) / k, lng: Math.round(p.lng * k) / k };
};

export const inBounds = (p: LatLng, b: Bounds) => p.lat >= b.south && p.lat <= b.north && p.lng >= b.west && p.lng <= b.east;

/** "-6.291500, 106.802000" trimmed to "-6.2915, 106.802": the form Google Maps also accepts back. */
export const formatLatLng = (p: LatLng) => `${p.lat}, ${p.lng}`;
